import { useMemo, useState, useCallback, useEffect } from "react";
import { Button, Card, CardBody, CardHeader, Row, Col } from "reactstrap";
import clsx from "clsx";

import { apiSlice } from "app/apiSlice";
import DataGrid from "components/DataGrid";
import { showToast } from "components/Toasts/helpers/showToast";
import useAuthorizationCheck from "hooks/useAuthorizationCheck";

import { BulkApprovalModal } from "./BulkApprovalModal";
import { createGroupHeaderRenderer } from "./GroupHeaderRenderers";
import { formatGridCurrency } from "../utils/formatters";
import { useApprovalsRowCount } from "../hooks/useApprovalsRowCount";

import { createApprovalsColDefs } from "../constants/approvalsColDefs";
import "./approvals-grid.scss";
import {
  transformApprovalData,
  isFullWidthRow,
  calculateApprovalSummary,
  type GridRowData,
} from "../utils/transformApprovalsData";

// Move constant outside component to prevent recreation on every render
const EMPTY_ARRAY: GridRowData[] = [];

interface ApprovalsGridProps {
  className?: string;
  gridId?: string;
}

/**
 * Approvals Grid component that displays milestones grouped by brand and campaign
 * using AG Grid Community features. Shows approval status for milestone payments.
 * Includes bulk approval functionality for approving/unapproving all milestones at once.
 */
export const ApprovalsGrid = ({
  className,
  gridId = "approvals",
}: ApprovalsGridProps) => {
  // Permission checks - approval_dashboard to view, approve_payment to take actions
  const canViewApprovals = useAuthorizationCheck(["approval_dashboard"]);
  const canApprovePayment = useAuthorizationCheck(["approve_payment"]);

  // State for bulk approval modals
  const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
  const [isBulkUnapproveModalOpen, setIsBulkUnapproveModalOpen] =
    useState(false);

  // Fetch forecast data for approvals using the forecast endpoint
  const {
    data: forecastData,
    isFetching,
    isLoading,
    refetch,
    error,
  } = apiSlice.useGetMilestonesForecastQuery(undefined);

  useEffect(() => {
    if (error) {
      showToast({
        type: "error",
        message: "Error fetching approvals data. Please refresh the page.",
      });
    }
  }, [error]);

  // Transform forecast data into grid rows with group headers
  const gridRowData = useMemo(() => {
    if (!forecastData || !Array.isArray(forecastData)) {
      return EMPTY_ARRAY;
    }

    return transformApprovalData(forecastData);
  }, [forecastData]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!forecastData || !Array.isArray(forecastData)) {
      return {
        totalCount: 0,
        approvedCount: 0,
        unapprovedCount: 0,
        totalAmount: 0,
        approvedAmount: 0,
        unapprovedAmount: 0,
        groupCount: 0,
      };
    }
    return calculateApprovalSummary(forecastData);
  }, [forecastData]);

  // Get all milestones from forecast data for bulk approval
  const allMilestones = useMemo(() => {
    if (!forecastData || !Array.isArray(forecastData)) {
      return [];
    }
    // Extract all milestones from all date groups
    return forecastData.reduce((acc, dateGroup) => {
      if (dateGroup.items && Array.isArray(dateGroup.items)) {
        acc.push(...dateGroup.items);
      }
      return acc;
    }, []);
  }, [forecastData]);

  // Handlers for bulk approval modals
  const handleApproveAllMilestones = useCallback(() => {
    setIsBulkApproveModalOpen(true);
  }, []);

  const handleUnapproveAllMilestones = useCallback(() => {
    setIsBulkUnapproveModalOpen(true);
  }, []);

  const handleCloseBulkApproveModal = useCallback(() => {
    setIsBulkApproveModalOpen(false);
  }, []);

  const handleCloseBulkUnapproveModal = useCallback(() => {
    setIsBulkUnapproveModalOpen(false);
  }, []);

  const handleBulkApprovalSuccess = useCallback(() => {
    setIsBulkApproveModalOpen(false);
    setIsBulkUnapproveModalOpen(false);
    refetch(); // Refresh the grid data after approval
    showToast({
      type: "success",
      message: "Bulk approval completed successfully!",
    });
  }, [refetch]);

  const columnDefs = useMemo(() => {
    return createApprovalsColDefs(canApprovePayment);
  }, [canApprovePayment]);

  // Bulk approval buttons for grid header
  const BulkApprovalButtons = useCallback(
    () => (
      <div className="d-flex gap-2">
        <Button
          color="success"
          size="sm"
          disabled={
            summaryStats.unapprovedCount === 0 || isLoading || isFetching
          }
          onClick={handleApproveAllMilestones}
          className="d-flex align-items-center"
        >
          Approve All ({summaryStats.unapprovedCount})
        </Button>
        <Button
          color="danger"
          size="sm"
          outline
          disabled={summaryStats.approvedCount === 0 || isLoading || isFetching}
          onClick={handleUnapproveAllMilestones}
          className="d-flex align-items-center"
        >
          Unapprove All ({summaryStats.approvedCount})
        </Button>
      </div>
    ),
    [
      summaryStats.unapprovedCount,
      summaryStats.approvedCount,
      isLoading,
      isFetching,
      handleApproveAllMilestones,
      handleUnapproveAllMilestones,
    ],
  );

  // Show permission error if user can't view approvals
  if (!canViewApprovals) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="text-center">
          <h4>Access Restricted</h4>
          <p className="text-muted">
            You don't have permission to view the approvals dashboard.
          </p>
        </div>
      </div>
    );
  }

  const classes = clsx("approvals-grid", "h-100", className);

  // AG Grid configuration for full width rows
  const gridProps = useMemo(
    () => ({
      isFullWidthRow,
      fullWidthCellRenderer: createGroupHeaderRenderer(),
      suppressSorting: true,
      defaultColDef: { sortable: false },
    }),
    [], // No dependencies needed - all values are static
  );

  return (
    <div className={clsx(classes, "d-flex flex-column h-100")}>
      {/* Summary Statistics */}
      <Card className="mb-3 py-2 flex-shrink-0">
        <CardBody className="py-1">
          <Row>
            <Col md={3}>
              <div className="text-center">
                <div className="h4">{summaryStats.unapprovedCount}</div>
                <small className="text-muted">Not Approved</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <div className="h4">{summaryStats.approvedCount}</div>
                <small className="text-muted">Approved</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <div className="h4">
                  {formatGridCurrency(summaryStats.unapprovedAmount)}
                </div>
                <small className="text-muted">Amount Not Approved</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <div className="h4">
                  {formatGridCurrency(summaryStats.approvedAmount)}
                </div>
                <small className="text-muted">Amount Approved</small>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Milestones Grid */}
      <div className="flex-grow-1 d-flex flex-column">
        <DataGrid
          appliedFilters={true}
          autoSizeEnabled={true}
          autoSizeIgnoreHeaders={false}
          autoSizeStrategy={{
            type: "fitCellContents",
          }}
          autoHeight={false}
          borders={true}
          columnDefs={columnDefs}
          gridId={gridId}
          idField="_id"
          isLoading={isLoading}
          isFetching={isFetching}
          refetch={refetch}
          quickFilter={false}
          rowData={gridRowData}
          // Bulk approval buttons in grid header
          HeaderActionElem={canApprovePayment ? <BulkApprovalButtons /> : null}
          suppressRowClickSelection
          suppressCellFocus
          suppressRowHoverHighlight
          suppressDragLeaveHidesColumns
          suppressRowDrag
          customRowCountHook={useApprovalsRowCount}
          {...gridProps}
        />
      </div>

      {/* Bulk Approval Modals */}
      <BulkApprovalModal
        isOpen={isBulkApproveModalOpen}
        onClose={handleCloseBulkApproveModal}
        milestones={allMilestones}
        onSuccess={handleBulkApprovalSuccess}
        actionType="approve"
        approvalType="all"
      />

      <BulkApprovalModal
        isOpen={isBulkUnapproveModalOpen}
        onClose={handleCloseBulkUnapproveModal}
        milestones={allMilestones}
        onSuccess={handleBulkApprovalSuccess}
        actionType="unapprove"
        approvalType="all"
      />
    </div>
  );
};
